import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  Panel,
} from "reactflow";
import "reactflow/dist/style.css";

interface CRMFormField {
  id: number | string;
  type: string;
  label: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface WhatsAppFlowMapping {
  id?: number;
  flow_id: string;
  crm_form_id: number;
  source_field: string;
  source_field_label?: string;
  target_field_id: string;
  target_field_label: string;
  mapping_type: "automatic" | "manual" | "keyword_based";
  is_active: boolean;
  keywords?: string[];
  mapping_order?: number;
  transformation_rule?: string;
  created_at?: string;
}

interface WhatsAppFlowMappingFlowProps {
  whatsappFields: string[];
  crmFormFields: CRMFormField[];
  existingMappings: WhatsAppFlowMapping[];
  onMappingsChange: (mappings: WhatsAppFlowMapping[]) => void;
  selectedFlowId: string;
  selectedCrmForm: any;
}

const WhatsAppFlowMappingFlow: React.FC<WhatsAppFlowMappingFlowProps> = ({
  whatsappFields,
  crmFormFields,
  existingMappings,
  onMappingsChange,
  selectedFlowId,
  selectedCrmForm,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Function to replace country field with location fields
  const replaceCountryWithLocationFields = useCallback(
    (fields: CRMFormField[]): CRMFormField[] => {
      const expandedFields: CRMFormField[] = [];

      fields.forEach((field) => {
        if (field.type === "country") {
          // Replace the country field with four location fields
          expandedFields.push({
            id: `${field.id}-country`,
            type: "select",
            label: "Country",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });

          expandedFields.push({
            id: `${field.id}-state`,
            type: "select",
            label: "State",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });

          expandedFields.push({
            id: `${field.id}-district`,
            type: "select",
            label: "District",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });

          expandedFields.push({
            id: `${field.id}-city`,
            type: "select",
            label: "City",
            required: field.required,
            typeCheck: field.typeCheck,
            options: [],
          });
        } else {
          expandedFields.push(field);
        }
      });

      return expandedFields;
    },
    []
  );

  // Initialize nodes
  useEffect(() => {
    const whatsappNodes: Node[] = whatsappFields.map((field, index) => ({
      id: `whatsapp-${field}`,
      type: "input",
      position: { x: 100, y: 100 + index * 80 },
      data: {
        label: field,
        type: "whatsapp",
        fieldName: field,
        description: "WhatsApp Flow Field",
      },
      style: {
        background: "#dcf8c6",
        border: "2px solid #25D366",
        borderRadius: "8px",
        padding: "10px",
        width: 180,
        fontSize: "12px",
      },
    }));

    const expandedCrmFields = replaceCountryWithLocationFields(crmFormFields);

    const crmNodes: Node[] = expandedCrmFields.map((field, index) => ({
      id: `crm-${field.id}`,
      type: "output",
      position: { x: 500, y: 100 + index * 80 },
      data: {
        label: `${field.label} ${field.required ? "*" : ""}`,
        type: "crm",
        fieldId: field.id,
        fieldType: field.type,
        required: field.required,
        description: field.typeCheck,
      },
      style: {
        background: field.required ? "#ffe6e6" : "#e6f7ff",
        border: `2px solid ${field.required ? "#ff4d4f" : "#1890ff"}`,
        borderRadius: "8px",
        padding: "10px",
        width: 180,
        fontSize: "12px",
      },
    }));

    setNodes([...whatsappNodes, ...crmNodes]);
  }, [whatsappFields, crmFormFields, replaceCountryWithLocationFields]);

  // Initialize edges from existing mappings
  useEffect(() => {
    const initialEdges: Edge[] = existingMappings.map((mapping) => ({
      id: `edge-${mapping.source_field}-${mapping.target_field_id}`,
      source: `whatsapp-${mapping.source_field}`,
      target: `crm-${mapping.target_field_id}`,
      style: {
        stroke:
          mapping.mapping_type === "automatic"
            ? "#52c41a"
            : mapping.mapping_type === "keyword_based"
            ? "#1890ff"
            : "#faad14",
        strokeWidth: 2,
      },
      animated: mapping.is_active,
      data: { mappingType: mapping.mapping_type },
    }));

    setEdges(initialEdges);
  }, [existingMappings]);

  const onConnect = useCallback(
    (params: Connection) => {
      const sourceField = params.source?.replace("whatsapp-", "");
      const targetFieldId = params.target?.replace("crm-", "");

      const expandedCrmFields = replaceCountryWithLocationFields(crmFormFields);
      const targetField = expandedCrmFields.find(
        (f) => f.id.toString() === targetFieldId
      );

      if (!sourceField || !targetField || !selectedCrmForm) {
        return;
      }

      // Check if this source field already has a mapping
      const existingMappingIndex = existingMappings.findIndex(
        (m) => m.source_field === sourceField
      );

      let updatedMappings: WhatsAppFlowMapping[];

      if (existingMappingIndex !== -1) {
        // Update existing mapping
        updatedMappings = existingMappings.map((mapping, index) =>
          index === existingMappingIndex
            ? {
                ...mapping,
                target_field_id: targetField.id.toString(),
                target_field_label: targetField.label,
                mapping_type: "manual",
              }
            : mapping
        );
      } else {
        // Create new mapping
        const newMapping: WhatsAppFlowMapping = {
          source_field: sourceField,
          source_field_label: sourceField,
          target_field_id: targetField.id.toString(),
          target_field_label: targetField.label,
          crm_form_id: selectedCrmForm.key,
          flow_id: selectedFlowId,
          mapping_type: "manual",
          is_active: true,
          keywords: [sourceField.toLowerCase()],
        };
        updatedMappings = [...existingMappings, newMapping];
      }

      // Update edges
      const newEdge = {
        ...params,
        id: `edge-${sourceField}-${targetFieldId}`,
        style: { stroke: "#faad14", strokeWidth: 2 },
        animated: true,
        data: { mappingType: "manual" },
      };

      setEdges((eds) => {
        const filteredEdges = eds.filter(
          (edge) => edge.source !== params.source
        );
        return addEdge(newEdge, filteredEdges);
      });

      // Update mappings
      onMappingsChange(updatedMappings);
    },
    [
      crmFormFields,
      existingMappings,
      onMappingsChange,
      selectedCrmForm,
      selectedFlowId,
      replaceCountryWithLocationFields,
    ]
  );

  // Handle edge deletion
  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      deletedEdges.forEach((edge) => {
        const sourceField = edge.source?.replace("whatsapp-", "");
        if (sourceField) {
          const updatedMappings = existingMappings.filter(
            (m) => m.source_field !== sourceField
          );
          onMappingsChange(updatedMappings);
        }
      });
    },
    [existingMappings, onMappingsChange]
  );

  // Custom node styling for MiniMap
  const nodeColor = (node: Node) => {
    switch (node.data.type) {
      case "whatsapp":
        return "#dcf8c6";
      case "crm":
        return node.data.required ? "#ffe6e6" : "#e6f7ff";
      default:
        return "#eee";
    }
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgesDelete={onEdgesDelete}
        fitView
        attributionPosition="bottom-right"
      >
        <Background />
        <Controls />
        <MiniMap
          nodeColor={nodeColor}
          nodeStrokeWidth={3}
          zoomable
          pannable
          style={{ background: "#f8f9fa" }}
        />
      </ReactFlow>
    </div>
  );
};

export default WhatsAppFlowMappingFlow;
